import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Home,
  Package,
  FolderOpen,
  BarChart3,
  Upload,
  Users,
  ShoppingCart,
  Receipt,
  FileText,
  Settings,
  Menu,
  X,
  TrendingUp,
  TrendingDown,
  Boxes,
  UserCog,
  Shield,
  Truck,
  ChevronDown,
  ChevronRight,
  DollarSign,
  ShoppingBag,
  PackageOpen,
  UserCheck
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { useAuthStore } from '../../stores/authStore'
import { useAuditorStore } from '../../stores/auditorStore'
import { usePermissions } from '../../hooks/usePermissions'

interface NavItem {
  icon: React.ComponentType<{ size?: number; className?: string }>
  label: string
  href: string
  permission?: string
}

interface NavGroup {
  label: string
  icon: React.ComponentType<{ size?: number; className?: string }>
  items: NavItem[]
}

// Auditor Navigation
const auditorNavigationGroups: (NavItem | NavGroup)[] = [
  // Dashboard
  { icon: Home, label: 'Dashboard', href: '/' },

  // Reports & Data
  {
    label: 'Reports & Data',
    icon: BarChart3,
    items: [
      { icon: FileText, label: 'GST Reports', href: '/reports/gst' },
      { icon: Receipt, label: 'Invoices', href: '/invoices' },
      { icon: TrendingUp, label: 'Sales Summary', href: '/reports/sales' },
    ]
  },
]

// Merchant Navigation
const merchantNavigationGroups: (NavItem | NavGroup)[] = [
  // Dashboard - standalone
  { icon: Home, label: 'Dashboard', href: '/', permission: 'dashboard.view' },

  // Sales Group
  {
    label: 'Sales',
    icon: DollarSign,
    items: [
      { icon: ShoppingCart, label: 'POS / Billing', href: '/pos', permission: 'sales.create' },
      { icon: Receipt, label: 'Sales Orders', href: '/orders', permission: 'sales.view' },
      { icon: FileText, label: 'Sales Invoices', href: '/invoices', permission: 'invoices.view' },
      { icon: Users, label: 'Customers', href: '/customers', permission: 'customers.view' },
    ]
  },

  // Purchase Group
  {
    label: 'Purchase',
    icon: ShoppingBag,
    items: [
      { icon: ShoppingBag, label: 'Purchase Orders', href: '/purchases', permission: 'purchases.view' },
      { icon: Truck, label: 'Suppliers', href: '/suppliers', permission: 'suppliers.view' },
      { icon: TrendingDown, label: 'Expenses', href: '/expenses', permission: 'expenses.view' },
    ]
  },

  // Inventory Group
  {
    label: 'Inventory',
    icon: PackageOpen,
    items: [
      { icon: Package, label: 'Products', href: '/products', permission: 'products.view' },
      { icon: FolderOpen, label: 'Categories', href: '/categories', permission: 'categories.view' },
      { icon: Boxes, label: 'Units', href: '/units', permission: 'products.view' },
      { icon: BarChart3, label: 'Stock Management', href: '/stock', permission: 'products.manage_stock' },
      { icon: Upload, label: 'Import/Export', href: '/import-export', permission: 'products.view' },
    ]
  },

  // Reports Group
  {
    label: 'Reports',
    icon: TrendingUp,
    items: [
      { icon: TrendingUp, label: 'Sales Reports', href: '/reports', permission: 'sales.view_reports' },
    ]
  },

  // Settings Group
  {
    label: 'Settings',
    icon: Settings,
    items: [
      { icon: UserCog, label: 'Users', href: '/users', permission: 'users.view' },
      { icon: Shield, label: 'Roles & Permissions', href: '/roles', permission: 'roles.view' },
      { icon: UserCheck, label: 'Auditor Management', href: '/settings/auditors', permission: 'users.manage_roles' },
      { icon: Settings, label: 'System Settings', href: '/settings', permission: 'settings.view' },
    ]
  },
]

function isNavGroup(item: NavItem | NavGroup): item is NavGroup {
  return 'items' in item
}

interface SidebarProps {
  className?: string
}

export const Sidebar = ({ className }: SidebarProps) => {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['Sales', 'Purchase', 'Inventory'])
  const location = useLocation()
  const { user } = useAuthStore()
  const { isAuditor } = useAuditorStore()
  const { hasPermission } = usePermissions()

  const toggleGroup = (label: string) => {
    setExpandedGroups(prev =>
      prev.includes(label)
        ? prev.filter(g => g !== label)
        : [...prev, label]
    )
  }

  // Choose navigation based on user type
  const navigationGroups = isAuditor ? auditorNavigationGroups : merchantNavigationGroups

  // Filter items based on permissions
  const hasVisibleItems = (items: NavItem[]) => {
    return items.some(item => !item.permission || hasPermission(item.permission))
  }

  const visibleNavigation = navigationGroups.filter(item => {
    if (isNavGroup(item)) {
      return hasVisibleItems(item.items)
    }
    return !item.permission || hasPermission(item.permission)
  })

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  return (
    <>
      {/* Mobile menu button - Fixed at top-left */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="fixed top-4 left-4 z-50 lg:hidden flex items-center justify-center w-10 h-10 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700"
      >
        <Menu className="w-5 h-5 text-gray-600 dark:text-gray-400" />
      </button>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "flex h-full flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 flex-shrink-0",
          // Desktop behavior
          "lg:flex",
          collapsed ? "w-16" : "w-64",
          // Mobile behavior - slide in from left
          "lg:relative fixed inset-y-0 left-0 z-50",
          mobileOpen ? "flex translate-x-0" : "hidden -translate-x-full lg:flex lg:translate-x-0",
          className
        )}
      >
        {/* Header */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg text-gray-900 dark:text-white">POS</span>
            </div>
          )}
          <button
            onClick={() => {
              setMobileOpen(false)
              setCollapsed(!collapsed)
            }}
            className="hidden lg:flex p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            {collapsed ? <ChevronRight className="w-5 h-5" /> : <X className="w-5 h-5" />}
          </button>
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-1">
          {visibleNavigation.map((item, index) => {
            if (isNavGroup(item)) {
              const isExpanded = expandedGroups.includes(item.label)
              const GroupIcon = item.icon

              return (
                <div key={index}>
                  {/* Group Header */}
                  <button
                    onClick={() => !collapsed && toggleGroup(item.label)}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                      "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700",
                      collapsed && "justify-center"
                    )}
                  >
                    <GroupIcon className="w-5 h-5 flex-shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-left">{item.label}</span>
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </>
                    )}
                  </button>

                  {/* Group Items */}
                  {!collapsed && isExpanded && (
                    <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 dark:border-gray-700 pl-2">
                      {item.items
                        .filter(navItem => !navItem.permission || hasPermission(navItem.permission))
                        .map((navItem, itemIndex) => {
                          const ItemIcon = navItem.icon
                          const active = isActive(navItem.href)

                          return (
                            <Link
                              key={itemIndex}
                              to={navItem.href}
                              onClick={() => setMobileOpen(false)}
                              className={cn(
                                "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                                active
                                  ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium"
                                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                              )}
                            >
                              <ItemIcon className="w-4 h-4 flex-shrink-0" />
                              <span>{navItem.label}</span>
                            </Link>
                          )
                        })}
                    </div>
                  )}
                </div>
              )
            } else {
              // Standalone item (e.g., Dashboard)
              const ItemIcon = item.icon
              const active = isActive(item.href)

              return (
                <Link
                  key={index}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    active
                      ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700",
                    collapsed && "justify-center"
                  )}
                >
                  <ItemIcon className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && <span>{item.label}</span>}
                </Link>
              )
            }
          })}
        </nav>

        {/* User info */}
        {!collapsed && user && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-medium text-sm">
                  {user.email?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user.email?.split('@')[0] || 'User'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {user.role || 'User'}
                </p>
              </div>
            </div>
          </div>
        )}
      </aside>
    </>
  )
}
